import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import {
  Article,
  ArticleCategory,
  articleService,
  CreateArticleData,
  UpdateArticleData,
} from "../../../services/Artikel";
import { articleCategoryService } from "../../../services/Artikel/Category";

const ArticleManagement = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateArticleData>({
    name: "",
    description: "",
    article_category_id: 0,
    image: undefined,
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await articleService.getArticles();
      setArticles(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch articles");
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await articleCategoryService.getArticleCategories();
      setCategories(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleCreateArticle = async () => {
    if (!formData.name.trim() || !formData.description.trim() || formData.article_category_id === 0) {
      setError("Please fill all required fields");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await articleService.createArticle({ ...formData, image: selectedFile || undefined });
      await fetchArticles();
      setSuccessMessage("Article created successfully");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to create article");
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateArticle = async () => {
    if (!editingArticle) return;

    try {
      const updateData: UpdateArticleData = {
        id: editingArticle.id,
        name: formData.name,
        description: formData.description,
        article_category_id: formData.article_category_id,
        image: selectedFile || undefined,
      };
      await articleService.updateArticle(updateData);
      await fetchArticles();
      setSuccessMessage("Article updated successfully");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to update article");
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteArticle = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;

    try {
      await articleService.deleteArticle(id);
      await fetchArticles();
      setSuccessMessage("Article deleted successfully");
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to delete article");
      setIsErrorModalOpen(true);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", article_category_id: 0, image: undefined });
    setSelectedFile(null);
    setPreviewUrl(null);
    setEditingArticle(null);
    setIsModalOpen(false);
  };

  // Filtered articles untuk search
  const filteredArticles = articles.filter((article) =>
    article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar/>

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">📄</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Article Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your article content</p>
                </div>
              </div>

              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Article
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">IMAGE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">NAME</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">CATEGORY</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">CREATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredArticles.map((article, index) => (
                    <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID menggunakan nomor urut (1, 2, 3, ...) */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>

                      <td className="px-8 py-6">
                        {article.image && (
                          <img
                            src={`${process.env.REACT_APP_API_URL || ''}${article.image}`}
                            alt={article.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        )}
                      </td>
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {article.name}
                      </td>
                      <td className="px-8 py-6 text-gray-600">
                        {article.article_category?.name || "-"}
                      </td>
                      <td className="px-8 py-6 text-gray-700 max-w-md">
                        {article.description.length > 120
                          ? article.description.substring(0, 120) + "..."
                          : article.description}
                      </td>
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(article.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingArticle(article);
                              setFormData({
                                name: article.name,
                                description: article.description,
                                article_category_id: article.article_category_id,
                                image: undefined,
                              });
                              setPreviewUrl(article.image);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredArticles.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-20 text-center text-gray-400">
                        No articles found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">
              {editingArticle ? "Edit Article" : "Add New Article"}
            </h2>

            <div className="space-y-6">
              <input
                type="text"
                placeholder="Article Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
              />

              <select
                value={formData.article_category_id}
                onChange={(e) => setFormData({ ...formData, article_category_id: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
              >
                <option value={0}>Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <textarea
                placeholder="Article Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-purple-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Article Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-2xl px-4 py-3"
                />
                {previewUrl && (
                  <img 
                    src={previewUrl.startsWith('data:') || previewUrl.startsWith('blob:') ? previewUrl : `${process.env.REACT_APP_API_URL || ''}${previewUrl}`} 
                    alt="Preview" 
                    className="mt-4 w-40 h-40 object-cover rounded-lg" 
                  />
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingArticle ? handleUpdateArticle : handleCreateArticle}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingArticle ? "Update Article" : "Create Article"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success & Error Notifications */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button 
              onClick={() => setIsSuccessModalOpen(false)} 
              className="bg-green-500 text-white px-10 py-3 rounded-2xl hover:bg-green-600"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button 
              onClick={() => setIsErrorModalOpen(false)} 
              className="bg-red-500 text-white px-10 py-3 rounded-2xl hover:bg-red-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleManagement;